
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Spinner,
  Text,
  WrapItem,
  SimpleGrid,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useColorModeValue
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import InfiniteScroll from 'react-infinite-scroll-component';
import { useGetVideoMusicsQuery } from "store/aiHub/aiGenerationApi";
import musicCategoryData from '../../utils/musicCategories.json';
import { FaPlay, FaPause } from "react-icons/fa";
import { SearchIcon, CloseIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { formatDuration } from "utils/functions";

// Audio Player
const CustomAudioPlayer = ({
  id,
  src,
  currentAudio,
  setCurrentAudio,
  currentAudioId,
  setCurrentAudioId,
  backgroundImage,
}) => {
  const audioRef = useRef(null);
  const isPlaying = currentAudioId === id;

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setCurrentAudio(null);
      setCurrentAudioId(null);
    } else {
      if (currentAudio && currentAudio !== audioRef.current) {
        currentAudio.pause();
      }
      audioRef.current.play();
      setCurrentAudio(audioRef.current);
      setCurrentAudioId(id);
    }
  };

  useEffect(() => {
    const ref = audioRef.current;
    if (!ref) return;

    const handleEnded = () => {
      setCurrentAudio(null);
      setCurrentAudioId(null);
    };

    ref.addEventListener("ended", handleEnded);
    return () => ref.removeEventListener("ended", handleEnded);
  }, [setCurrentAudio, setCurrentAudioId]);

  return (
    <>
      <audio ref={audioRef} src={src} hidden />
      <IconButton
        icon={isPlaying ? <FaPause /> : <FaPlay />}
        onClick={togglePlay}
        aria-label="Play/Pause"
        size="md"
        variant="solid"
        borderRadius="full"
        _hover={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundColor: 'transparent',
        }}
        sx={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
        }}
      />
    </>
  );
};

export default function MusicTabPanel({ selectedTab, selectedMusic, onSelectMusic }) {

  const bgColor = useColorModeValue('#fff', 'none');
  const hoverColor = useColorModeValue('brand.100', 'purple.800');
  const checkColor = useColorModeValue('brand.800', 'gray.50');
  const checkBg = useColorModeValue('brand.200', 'purple.500');

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [page, setPage] = useState(1);
  const [musics, setMusics] = useState([]);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentAudioId, setCurrentAudioId] = useState(null);
  const [searchActive, setSearchActive] = useState(false);

  const { data, isLoading, isFetching } = useGetVideoMusicsQuery(
    selectedTab === "music"
      ? {
        category: selectedCategory === "All" ? undefined : selectedCategory,
        search: debouncedSearchTerm.length > 2 ? debouncedSearchTerm : undefined,
        page,
        page_size: 10,
      }
      : undefined,
    {
      skip: selectedTab !== "music",
      refetchOnMountOrArgChange: true,
    }
  );

  const handleSearchFocus = () => setSearchActive(true);
  const handleSearchBlur = () => {
    if (!searchTerm) setSearchActive(false);  // keep expanded if has search text
  };

  useEffect(() => {
    if (searchTerm.length > 2) {
      const handler = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
      }, 400);

      return () => clearTimeout(handler);
    } else {
      // If search term is 2 or fewer chars, reset debounced search term to empty string
      setDebouncedSearchTerm("");
    }
  }, [searchTerm]);


  // When data changes, append new musics (pagination)
  useEffect(() => {
    if (page === 1 && data?.results) {
      setMusics(data.results);
    } else if (page > 1 && data?.results) {
      setMusics((prevMusics) => [...prevMusics, ...data.results]);
    }
  }, [data, page]);

  // Infinite scroll: detect when near bottom to load more pages
  const loadMoreMusics = () => {
    if (!isFetching) setPage((prev) => prev + 1);
  };

  // Reset musics and page on category or search change
  useEffect(() => {
    setPage(1);
  }, [selectedCategory, debouncedSearchTerm]);

  const hasMore = data?.count ? page * 10 < data.count : false;

  return (
    <Box w={'full'}>
      <Flex
        alignItems="center"
        mb={2}
        width="100%"
        overflow="hidden"
        gap={2} // Adds consistent spacing between elements
      >
        {/* Categories with scroll */}
        <Flex
          overflowX="auto"
          whiteSpace="nowrap"
          gap={2}
          flex="1"
          alignItems="center"
          sx={{
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {musicCategoryData.musicCategories.map(({ name }) => (
            <WrapItem key={name}>
              <Button
                size="xs"
                variant={selectedCategory === name ? 'solid' : 'outline'}
                colorScheme='brand'
                onClick={() => setSelectedCategory(name)}
                whiteSpace="nowrap"
              >
                {name}
              </Button>
            </WrapItem>
          ))}
        </Flex>

        {/* Chevron icon after category scroll */}
        <Flex flexShrink={0}>
          <Icon as={ChevronRightIcon} boxSize={4} color="gray.500" />
        </Flex>

        {/* Search input */}
        <Flex flexBasis={searchActive ? '35%' : '25%'} flexShrink={0} transition="flex-basis 0.3s ease">
          <InputGroup size="sm" width="100%">
            <InputLeftElement pointerEvents="none" children={<SearchIcon color="gray.300" />} />
            <Input
              size="sm"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              borderRadius="md"
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
            />
            {searchTerm && (
              <InputRightElement>
                <IconButton
                  size="xs"
                  aria-label="Clear search"
                  icon={<CloseIcon />}
                  onClick={() => setSearchTerm('')}
                  variant="none"
                  tabIndex={-1}
                />
              </InputRightElement>
            )}
          </InputGroup>
        </Flex>
      </Flex>

      {/* Music List with infinite scroll */}
      {isLoading ?
        <Flex alignItems="center" justifyContent="center" h="50vh">
          <Spinner size="md" />
        </Flex>
        :
        <>
          {(musics.length === 0 && !isLoading) && (
            <Flex alignItems="center" justifyContent="center" h="50vh">
              <Text fontSize={'14px'}>No music found for this category.</Text>
            </Flex>
          )}
          <Box id="scrollableMusicDiv" maxH="400px" overflowY="auto" p={4}>
            <InfiniteScroll
              dataLength={musics.length}
              next={loadMoreMusics}
              hasMore={hasMore}
              loader={
                <Flex justify="center" p={2}>
                  <Spinner size="sm" />
                </Flex>
              }
              scrollableTarget="scrollableMusicDiv"
            >
              <SimpleGrid columns={[1, 2]} spacing={4}>
                {musics.map((music) => (
                  <Flex
                    key={music.id}
                    border="1px solid"
                    borderColor={selectedMusic?.id === music.id ? checkColor : "brand.200"}
                    bg={selectedMusic?.id === music.id ? checkBg : "transparent"}
                    p={2}
                    borderRadius="md"
                    width="100%"
                    gap={4}
                    alignItems="center"
                    cursor="pointer"
                    transition="background 0.2s"
                    _hover={{ bg: hoverColor, cursor: "pointer" }}
                    onClick={() => {
                      onSelectMusic?.(music);
                    }}
                  >

                    <CustomAudioPlayer
                      id={music.id}
                      src={music.url}
                      backgroundImage={music.cover_url}
                      currentAudio={currentAudio}
                      setCurrentAudio={setCurrentAudio}
                      currentAudioId={currentAudioId}
                      setCurrentAudioId={setCurrentAudioId}
                    />
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                        {music.name.replace(".mp3", "").trim()}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {formatDuration(music.duration)}
                      </Text>
                    </Box>

                  </Flex>
                ))}
              </SimpleGrid>
            </InfiniteScroll>
          </Box>
        </>
      }
    </Box>
  );
}
